"use client";

import { c as _c } from "react/compiler-runtime";
import type { InitialConfigType } from "@lexical/react/LexicalComposer";
import type { FocusEvent } from "react";
import { LexicalComposer } from "@lexical/react/LexicalComposer";
import { forwardRef, useImperativeHandle, useCallback, useEffect, useRef } from "react";
import type { EditorState } from "lexical";
import { RootNode, COMMAND_PRIORITY_CRITICAL, createCommand } from "lexical";
import { RichTextPlugin } from "@lexical/react/LexicalRichTextPlugin";
import { ContentEditable } from "@lexical/react/LexicalContentEditable";
import LexicalErrorBoundary from "@lexical/react/LexicalErrorBoundary";
import { HistoryPlugin } from "@lexical/react/LexicalHistoryPlugin";
import { OnChangePlugin } from "@lexical/react/LexicalOnChangePlugin";
import { MarkdownShortcutPlugin } from "@lexical/react/LexicalMarkdownShortcutPlugin";
import { TabIndentationPlugin } from "@lexical/react/LexicalTabIndentationPlugin";
import { ListNode, ListItemNode } from "@lexical/list";
import { HeadingNode, QuoteNode } from "@lexical/rich-text";
import { HorizontalRuleNode } from "@lexical/react/LexicalHorizontalRuleNode";
import { ListPlugin } from "@lexical/react/LexicalListPlugin";
import { TableCellNode, TableNode, TableRowNode } from "@lexical/table";
import { CodeHighlightNode, CodeNode } from "@lexical/code";
import { AutoLinkNode, LinkNode } from "@lexical/link";
import { LinkPlugin } from "@lexical/react/LexicalLinkPlugin";
import type { MentionedUser, MentionedWave, ReferencedNft } from "@/entities/IDrop";
import { ActiveDropAction } from "@/types/dropInteractionTypes";
import { MentionNode } from "../drops/create/lexical/nodes/MentionNode";
import { GroupMentionNode } from "../drops/create/lexical/nodes/GroupMentionNode";
import { HashtagNode } from "../drops/create/lexical/nodes/HashtagNode";
import { WaveMentionNode } from "../drops/create/lexical/nodes/WaveMentionNode";
import { ImageNode } from "../drops/create/lexical/nodes/ImageNode";
import ExampleTheme from "../drops/create/lexical/ExampleTheme";
import { assertUnreachable } from "@/helpers/AllowlistToolHelpers";
import type { ClearEditorPluginHandles } from "../drops/create/lexical/plugins/ClearEditorPlugin";
import ClearEditorPlugin from "../drops/create/lexical/plugins/ClearEditorPlugin";
import type { NewMentionsPluginHandles } from "../drops/create/lexical/plugins/mentions/MentionsPlugin";
import NewMentionsPlugin from "../drops/create/lexical/plugins/mentions/MentionsPlugin";
import type { NewHastagsPluginHandles } from "../drops/create/lexical/plugins/hashtags/HashtagsPlugin";
import NewHashtagsPlugin from "../drops/create/lexical/plugins/hashtags/HashtagsPlugin";
import type { NewWaveMentionsPluginHandles } from "../drops/create/lexical/plugins/waves/WaveMentionsPlugin";
import NewWaveMentionsPlugin from "../drops/create/lexical/plugins/waves/WaveMentionsPlugin";
import { MaxLengthPlugin } from "../drops/create/lexical/plugins/MaxLengthPlugin";
import DragDropPastePlugin from "../drops/create/lexical/plugins/DragDropPastePlugin";
import EnterKeyPlugin from "../drops/create/lexical/plugins/enter/EnterKeyPlugin";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import CreateDropEmojiPicker from "./CreateDropEmojiPicker";
import useCapacitor from "@/hooks/useCapacitor";
import EmojiPlugin from "../drops/create/lexical/plugins/emoji/EmojiPlugin";
import { EmojiNode } from "../drops/create/lexical/nodes/EmojiNode";
import { SAFE_MARKDOWN_TRANSFORMERS } from "@/components/drops/create/lexical/transformers/markdownTransformers";
import PlainTextPastePlugin from "@/components/drops/create/lexical/plugins/PlainTextPastePlugin";
export interface CreateDropInputHandles {
  clearEditorState: () => void;
  focus: () => void;
}

// Create a custom command
const DISABLE_EDIT_COMMAND = createCommand("DISABLE_EDIT");

// Create a custom plugin to handle disabling
function DisableEditPlugin(t0) {
  const $ = _c(4);
  const {
    disabled
  } = t0;
  const [editor] = useLexicalComposerContext();
  let t1;
  let t2;
  if ($[0] !== disabled || $[1] !== editor) {
    t1 = () => {
      if (disabled) {
        return editor.registerCommand(DISABLE_EDIT_COMMAND, _temp, COMMAND_PRIORITY_CRITICAL);
      }
      return _temp2;
    };
    t2 = [editor, disabled];
    $[0] = disabled;
    $[1] = editor;
    $[2] = t1;
    $[3] = t2;
  } else {
    t1 = $[2];
    t2 = $[3];
  }
  useEffect(t1, t2);
  return null;
}
function _temp2() {}
function _temp() {
  return true;
}
const CreateDropInput = forwardRef<CreateDropInputHandles, {
  readonly waveId: string;
  readonly editorState: EditorState | null;
  readonly type: ActiveDropAction | null;
  readonly canSubmit: boolean;
  readonly isStormMode: boolean;
  readonly submitting: boolean;
  readonly isDropMode: boolean;
  readonly canMentionAll?: boolean | undefined;
  readonly onDrop?: (() => void) | undefined;
  readonly onEditorState: (editorState: EditorState) => void;
  readonly onEditorBlur?: (event: FocusEvent<HTMLDivElement>) => void;
  readonly onReferencedNft: (referencedNft: ReferencedNft) => void;
  readonly onMentionedUser: (mentionedUser: Omit<MentionedUser, "current_handle">) => void;
  readonly onMentionedWave: (mentionedWave: MentionedWave) => void;
}>((t0, ref) => {
  const $ = _c(79);
  const {
    waveId,
    editorState,
    type,
    canSubmit,
    isStormMode,
    isDropMode,
    canMentionAll: t1,
    submitting,
    onEditorState,
    onEditorBlur,
    onReferencedNft,
    onMentionedUser,
    onMentionedWave,
    onDrop
  } = t0;
  const canMentionAll = t1 === undefined ? false : t1;
  const {
    isCapacitor
  } = useCapacitor();
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = [MentionNode, GroupMentionNode, HashtagNode, WaveMentionNode, RootNode, HeadingNode, ListNode, ListItemNode, QuoteNode, CodeNode, CodeHighlightNode, TableNode, TableCellNode, TableRowNode, AutoLinkNode, LinkNode, HorizontalRuleNode, ImageNode, EmojiNode];
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  const t3 = !submitting;
  let t4;
  if ($[1] !== editorState || $[2] !== t3) {
    t4 = {
      namespace: "User Drop",
      nodes: t2,
      editorState,
      editable: t3,
      onError(error) {
        throw error;
      },
      theme: ExampleTheme
    };
    $[1] = editorState;
    $[2] = t3;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  const editorConfig = t4;
  let t5;
  if ($[4] !== onEditorState) {
    t5 = editorState_0 => onEditorState(editorState_0);
    $[4] = onEditorState;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  const onEditorStateChange = t5;
  let t6;
  if ($[6] !== onMentionedUser) {
    t6 = user => {
      onMentionedUser(user);
    };
    $[6] = onMentionedUser;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  const onMentionedUserAdded = t6;
  let t7;
  if ($[8] !== onMentionedWave) {
    t7 = wave => {
      onMentionedWave(wave);
    };
    $[8] = onMentionedWave;
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  const onMentionedWaveAdded = t7;
  let t8;
  if ($[10] !== onReferencedNft) {
    t8 = hashtag => onReferencedNft(hashtag);
    $[10] = onReferencedNft;
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  const onHashtagAdded = t8;
  let t9;
  if ($[12] !== isDropMode || $[13] !== isStormMode || $[14] !== type) {
    t9 = () => {
      if (isStormMode) {
        return "Add to the storm";
      }
      if (type === null) {
        return isDropMode ? "Create a drop" : "Create a post";
      }
      switch (type) {
        case ActiveDropAction.REPLY:
          {
            return isDropMode ? "Drop a reply" : "Post a reply";
          }
        case ActiveDropAction.QUOTE:
          {
            return isDropMode ? "Quote a drop" : "Post a quote";
          }
        default:
          {
            assertUnreachable(type);
            return "";
          }
      }
    };
    $[12] = isDropMode;
    $[13] = isStormMode;
    $[14] = type;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  const getPlaceHolderText = t9;
  let t10;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    const urlRegExp = new RegExp(/((([A-Za-z]{3,9}:(?:\/\/)?)(?:[-;:&=+$,\w]+@)?[A-Za-z0-9.-]+|(?:www.|[-;:&=+$,\w]+@)[A-Za-z0-9.-]+)((?:\/[+~%/.\w-_]*)?\??(?:[-+=&;%@.\w_]*)#?(?:[\w]*))?)/);
    t10 = function validateUrl(url) {
      return url === "https://" || urlRegExp.test(url);
    };
    $[16] = t10;
  } else {
    t10 = $[16];
  }
  const validateUrl = t10;
  const clearEditorRef = useRef(null);
  const editorRef = useRef(null);
  let t11;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = () => {
      clearEditorRef.current?.clearEditorState();
    };
    $[17] = t11;
  } else {
    t11 = $[17];
  }
  const clearEditorState = t11;
  let t12;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = () => ({
      clearEditorState,
      focus: () => {
        (editorRef.current?.querySelector("[contenteditable=\"true\"]") as HTMLElement).focus();
      }
    });
    $[18] = t12;
  } else {
    t12 = $[18];
  }
  useImperativeHandle(ref, t12);
  const mentionsPluginRef = useRef(null);
  let t13;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = () => !!mentionsPluginRef.current?.isMentionsOpen();
    $[19] = t13;
  } else {
    t13 = $[19];
  }
  const isMentionsOpen = t13;
  const hashtagPluginRef = useRef(null);
  let t14;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = () => !!hashtagPluginRef.current?.isHashtagsOpen();
    $[20] = t14;
  } else {
    t14 = $[20];
  }
  const isHashtagsOpen = t14;
  const waveMentionsPluginRef = useRef(null);
  let t15;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    const isWaveMentionsOpen = () => !!waveMentionsPluginRef.current?.isWaveMentionsOpen();
    t15 = () => !isMentionsOpen() && !isHashtagsOpen() && !isWaveMentionsOpen();
    $[21] = t15;
  } else {
    t15 = $[21];
  }
  const canSubmitWithEnter = t15;
  const canSubmitRef = useRef(canSubmit);
  const onDropRef = useRef(onDrop);
  let t16;
  let t17;
  if ($[22] !== canSubmit) {
    t16 = () => {
      canSubmitRef.current = canSubmit;
    };
    t17 = [canSubmit];
    $[22] = canSubmit;
    $[23] = t16;
    $[24] = t17;
  } else {
    t16 = $[23];
    t17 = $[24];
  }
  useEffect(t16, t17);
  let t18;
  let t19;
  if ($[25] !== onDrop) {
    t18 = () => {
      onDropRef.current = onDrop;
    };
    t19 = [onDrop];
    $[25] = onDrop;
    $[26] = t18;
    $[27] = t19;
  } else {
    t18 = $[26];
    t19 = $[27];
  }
  useEffect(t18, t19);
  let t20;
  if ($[28] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = () => {
      if (!canSubmitRef.current || !onDropRef.current) {
        return;
      }
      onDropRef.current();
    };
    $[28] = t20;
  } else {
    t20 = $[28];
  }
  const handleSubmit = t20;
  let t21;
  if ($[29] === Symbol.for("react.memo_cache_sentinel")) {
    t21 = {
      touchAction: "manipulation"
    };
    $[29] = t21;
  } else {
    t21 = $[29];
  }
  let t22;
  if ($[30] !== submitting) {
    t22 = e => {
      const target = e.currentTarget;
      if (!submitting) {
        Promise.resolve().then(() => {
          target.focus();
          const selection = window.getSelection();
          if (selection?.rangeCount === 0) {
            const range = document.createRange();
            range.selectNodeContents(target);
            range.collapse(false);
            selection.addRange(range);
          }
        });
      }
    };
    $[30] = submitting;
    $[31] = t22;
  } else {
    t22 = $[31];
  }
  const t23 = `editor-input-one-liner tw-form-input tw-block tw-max-h-[40vh] tw-w-full tw-resize-none tw-rounded-lg tw-border-0 tw-bg-iron-900 tw-py-2.5 tw-pl-3 tw-text-base tw-font-normal tw-leading-6 tw-text-white tw-caret-primary-400 tw-shadow-sm tw-ring-1 tw-ring-inset tw-ring-iron-700 tw-transition tw-duration-300 tw-ease-out tw-scrollbar-thin tw-scrollbar-track-iron-900 tw-scrollbar-thumb-iron-600 placeholder:tw-text-iron-500 focus:tw-bg-iron-950 focus:tw-outline-none focus:tw-ring-1 focus:tw-ring-inset focus:tw-ring-primary-400 sm:tw-text-sm ${submitting ? "tw-cursor-default tw-opacity-50" : ""} ${isCapacitor ? "tw-pr-[35px]" : "tw-pr-[40px]"}`;
  let t24;
  if ($[32] !== onEditorBlur || $[33] !== t22 || $[34] !== t23) {
    t24 = <ContentEditable spellCheck={true} autoCorrect="on" style={t21} onClick={t22} onBlur={onEditorBlur} className={t23} />;
    $[32] = onEditorBlur;
    $[33] = t22;
    $[34] = t23;
    $[35] = t24;
  } else {
    t24 = $[35];
  }
  let t25;
  if ($[36] === Symbol.for("react.memo_cache_sentinel")) {
    t25 = <CreateDropEmojiPicker />;
    $[36] = t25;
  } else {
    t25 = $[36];
  }
  let t26;
  if ($[37] !== t24) {
    t26 = <div className="tw-relative">{t24}{t25}</div>;
    $[37] = t24;
    $[38] = t26;
  } else {
    t26 = $[38];
  }
  const t27 = `editor-placeholder ${submitting ? "tw-opacity-50" : ""}`;
  const t28 = getPlaceHolderText();
  let t29;
  if ($[39] !== t27 || $[40] !== t28) {
    t29 = <span className={t27}>{t28}</span>;
    $[39] = t27;
    $[40] = t28;
    $[41] = t29;
  } else {
    t29 = $[41];
  }
  let t30;
  if ($[42] !== t26 || $[43] !== t29) {
    t30 = <RichTextPlugin contentEditable={t26} placeholder={t29} ErrorBoundary={LexicalErrorBoundary} />;
    $[42] = t26;
    $[43] = t29;
    $[44] = t30;
  } else {
    t30 = $[44];
  }
  let t31;
  if ($[45] === Symbol.for("react.memo_cache_sentinel")) {
    t31 = <HistoryPlugin />;
    $[45] = t31;
  } else {
    t31 = $[45];
  }
  let t32;
  if ($[46] !== onEditorStateChange) {
    t32 = <OnChangePlugin onChange={onEditorStateChange} />;
    $[46] = onEditorStateChange;
    $[47] = t32;
  } else {
    t32 = $[47];
  }
  let t33;
  if ($[48] !== canMentionAll || $[49] !== onMentionedUserAdded || $[50] !== waveId) {
    t33 = <NewMentionsPlugin waveId={waveId} onSelect={onMentionedUserAdded} canMentionAll={canMentionAll} ref={mentionsPluginRef} />;
    $[48] = canMentionAll;
    $[49] = onMentionedUserAdded;
    $[50] = waveId;
    $[51] = t33;
  } else {
    t33 = $[51];
  }
  let t34;
  if ($[52] !== onMentionedWaveAdded) {
    t34 = <NewWaveMentionsPlugin onSelect={onMentionedWaveAdded} ref={waveMentionsPluginRef} />;
    $[52] = onMentionedWaveAdded;
    $[53] = t34;
  } else {
    t34 = $[53];
  }
  let t35;
  if ($[54] !== onHashtagAdded) {
    t35 = <NewHashtagsPlugin onSelect={onHashtagAdded} ref={hashtagPluginRef} />;
    $[54] = onHashtagAdded;
    $[55] = t35;
  } else {
    t35 = $[55];
  }
  let t36;
  let t37;
  let t38;
  let t39;
  let t40;
  let t41;
  let t42;
  let t43;
  if ($[56] === Symbol.for("react.memo_cache_sentinel")) {
    t36 = <MaxLengthPlugin maxLength={25000} />;
    t37 = <DragDropPastePlugin />;
    t38 = <ListPlugin />;
    t39 = <PlainTextPastePlugin />;
    t40 = <MarkdownShortcutPlugin transformers={SAFE_MARKDOWN_TRANSFORMERS} />;
    t41 = <TabIndentationPlugin />;
    t42 = <LinkPlugin validateUrl={validateUrl} />;
    t43 = <ClearEditorPlugin ref={clearEditorRef} />;
    $[56] = t36;
    $[57] = t37;
    $[58] = t38;
    $[59] = t39;
    $[60] = t40;
    $[61] = t41;
    $[62] = t42;
    $[63] = t43;
  } else {
    t36 = $[56];
    t37 = $[57];
    t38 = $[58];
    t39 = $[59];
    t40 = $[60];
    t41 = $[61];
    t42 = $[62];
    t43 = $[63];
  }
  let t44;
  let t45;
  if ($[64] !== submitting) {
    t44 = <DisableEditPlugin disabled={submitting} />;
    t45 = <EnterKeyPlugin handleSubmit={handleSubmit} canSubmitWithEnter={canSubmitWithEnter} disabled={submitting} />;
    $[64] = submitting;
    $[65] = t44;
    $[66] = t45;
  } else {
    t44 = $[65];
    t45 = $[66];
  }
  let t46;
  if ($[67] === Symbol.for("react.memo_cache_sentinel")) {
    t46 = <EmojiPlugin />;
    $[67] = t46;
  } else {
    t46 = $[67];
  }
  let t47;
  if ($[68] !== t30 || $[69] !== t32 || $[70] !== t33 || $[71] !== t34 || $[72] !== t35 || $[73] !== t44 || $[74] !== t45) {
    t47 = <div className="tw-flex tw-items-end tw-gap-x-3"><div className="tw-relative tw-w-full">{t30}{t31}{t32}{t33}{t34}{t35}{t36}{t37}{t38}{t39}{t40}{t41}{t42}{t43}{t44}{t45}{t46}</div></div>;
    $[68] = t30;
    $[69] = t32;
    $[70] = t33;
    $[71] = t34;
    $[72] = t35;
    $[73] = t44;
    $[74] = t45;
    $[75] = t47;
  } else {
    t47 = $[75];
  }
  let t48;
  if ($[76] !== editorConfig || $[77] !== t47) {
    t48 = <div className="tailwind-scope" ref={editorRef}><LexicalComposer initialConfig={editorConfig}>{t47}</LexicalComposer></div>;
    $[76] = editorConfig;
    $[77] = t47;
    $[78] = t48;
  } else {
    t48 = $[78];
  }
  return t48;
});
CreateDropInput.displayName = "CreateDropInput";
export default CreateDropInput;